import React, { useState } from "react";
import { Link } from "react-router-dom";
import PublicPage from "@/components/public/PublicPage";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

const QUESTIONS = [
  { q: "Are fridge and freezer temperatures recorded every day?", gap: "Daily fridge/freezer readings are missing or irregular." },
  { q: "Are hot-holding and cooling temperatures written down?", gap: "Hot-holding and cooling temperatures aren't recorded." },
  { q: "Are delivery temperatures checked and logged on arrival?", gap: "Deliveries aren't checked and logged." },
  { q: "Do opening and closing checks have a name and time on them?", gap: "Opening/closing checks can't be tied to a person and time." },
  { q: "When a reading is out of range, is the corrective action recorded?", gap: "Out-of-range readings have no recorded corrective action." },
  { q: "Is cleaning signed off on a schedule?", gap: "Cleaning isn't signed off on a schedule." },
  { q: "Could you show an inspector the last 30 days in under 5 minutes?", gap: "Records can't be produced quickly for an inspector." },
  { q: "Are records safe from being lost, wet or missing pages?", gap: "Paper records are at risk of loss or damage." },
];

export default function AuditCheck() {
  const [answers, setAnswers] = useState<(boolean | null)[]>(QUESTIONS.map(() => null));
  const done = answers.every((a) => a !== null);
  const yes = answers.filter((a) => a === true).length;
  const score = Math.round((yes / QUESTIONS.length) * 100);
  const gaps = QUESTIONS.filter((_, i) => answers[i] === false);

  const set = (i: number, v: boolean) => setAnswers((p) => p.map((a, j) => (j === i ? v : a)));

  return (
    <PublicPage
      eyebrow="Free 2-minute check"
      title="How ready is your kitchen for a food safety inspection?"
      intro="Answer 8 yes/no questions about your records. You'll get a readiness score and the gaps an inspector is most likely to spot. No sign-up needed."
    >
      <section className="max-w-3xl mx-auto px-4 pb-20 space-y-4">
        {QUESTIONS.map((item, i) => (
          <div key={item.q} className="app-card p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <p className="text-white/85 text-sm">{i + 1}. {item.q}</p>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant={answers[i] === true ? "default" : "outline"} onClick={() => set(i, true)}>Yes</Button>
              <Button size="sm" variant={answers[i] === false ? "default" : "outline"} onClick={() => set(i, false)}>No</Button>
            </div>
          </div>
        ))}

        {done && (
          <div className="app-card p-6 space-y-4" aria-live="polite">
            <p className="text-white text-2xl font-semibold">Your readiness score: {score}/100</p>
            {gaps.length === 0 ? (
              <p className="flex gap-2 text-white/80"><CheckCircle2 className="h-5 w-5 text-crystal-orange" /> Strong records. Going digital keeps them that way with less effort.</p>
            ) : (
              <ul className="space-y-2">
                {gaps.map((g) => (
                  <li key={g.q} className="flex gap-2 text-white/80 text-sm">
                    <AlertTriangle className="h-4 w-4 text-crystal-orange shrink-0 mt-0.5" /> {g.gap}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-white/70 text-sm">Crystal HACCP covers every one of these on iPhone, iPad and Android, with ready-made daily checklists from day one.</p>
            <Button asChild size="lg" className="bg-crystal-orange hover:bg-crystal-orange/90">
              <Link to="/auth?mode=signup">Fix these gaps — start 7-day free trial</Link>
            </Button>
          </div>
        )}
      </section>
    </PublicPage>
  );
}
